import { type Category, dec, HOP_FORM_LABELS, ZERO } from "@bl/domain";
import { Plus, Search } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  type CatalogProduct,
  type Item,
  type RecipeLine,
  send,
  useCatalog,
  useChange,
  useItems,
} from "../api";
import { Dialog, ErrorNote, onTabKeys } from "../components/ui";
import { hopDetail, showOnHand } from "../format";
import { NewItemForm } from "../forms/ItemDialogs";
import { useLayout } from "../layout/LayoutProvider";
import { itemMatches, wordsOf } from "../search";
import {
  blankLine,
  type DraftLine,
  KIND_CATEGORIES,
  KIND_LABELS,
  type Kind,
  lineFromItem,
  shownName,
} from "./recipeDraft";

/**
 * One search for an ingredient (Tyler, 2026-09-30): what is on hand listed before anything is typed,
 * then inventory and the catalog together, stocked first, and anything typable by hand. A catalog
 * product is added to inventory as it is picked, so the line is linked from the start.
 *
 * Something in neither is created there and then, with its details, and saved to inventory so it is
 * picked like anything else next time (Scott's Jam, 2026-10-02: Omega's yeasts, which none of the
 * catalog's suppliers sell). A name for this recipe alone is still one press away.
 *
 * Given a line to replace, it is the same search for an item of that line's kind, the recipe page's
 * suggestion on top, and the line made from what is picked takes its place.
 */
export function IngredientPicker({
  open,
  onClose,
  onPick,
  replacing = null,
  suggestion = null,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (line: DraftLine) => void;
  replacing?: DraftLine | null;
  suggestion?: RecipeLine["suggestion"];
}) {
  const [chosen, setKind] = useState<Kind>("fermentable");
  const kind = replacing?.kind ?? chosen;
  return (
    <ItemSearch
      open={open}
      onClose={onClose}
      kind={kind}
      onKind={replacing ? undefined : setKind}
      title={replacing ? `Replace ${shownName(replacing)}` : "Add an ingredient"}
      note={
        replacing
          ? "It keeps its amount and timing in this recipe, and takes the name and figures of the item you pick."
          : undefined
      }
      suggestion={suggestion}
      saveLabel={replacing ? "Save and replace" : "Save and add"}
      onItem={(it) => onPick(lineFromItem(kind, it))}
      onNameOnly={replacing ? undefined : (name) => onPick(blankLine(kind, name))}
    />
  );
}
/**
 * Which item a recipe page's line is made with, found by typing (Scott's Jam, 2026-10-12: "I should
 * be able to type out flake and it should show me all the things the system knows with flake"): the
 * builder's search, which narrows as it is typed and lists the catalog too. Linking leaves the line's
 * own name and figures alone (Tyler, 2026-10-01).
 */
export function LinkPicker({
  open,
  onClose,
  line,
  onItem,
}: {
  open: boolean;
  onClose: () => void;
  line: RecipeLine;
  onItem: (item: Item) => void;
}) {
  return (
    <ItemSearch
      open={open}
      onClose={onClose}
      kind={line.kind}
      title={`Link ${line.name} to an item`}
      note="It goes by the item's name and keeps its figures in this recipe. Brew day takes from the item's lots."
      suggestion={line.suggestion}
      saveLabel="Save and link"
      onItem={onItem}
    />
  );
}
/**
 * The search both pickers are: an item of one kind from inventory or the catalog, or a new one. With
 * onKind, the kind is chosen on tabs; with onNameOnly, a name for this recipe alone is offered too.
 */
function ItemSearch({
  open,
  onClose,
  kind,
  onKind,
  title,
  note,
  suggestion = null,
  saveLabel,
  onItem,
  onNameOnly,
}: {
  open: boolean;
  onClose: () => void;
  kind: Kind;
  onKind?: (kind: Kind) => void;
  title: string;
  note?: string;
  suggestion?: RecipeLine["suggestion"];
  saveLabel: string;
  onItem: (item: Item) => void;
  onNameOnly?: (name: string) => void;
}) {
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const searchId = useId();
  const { data: items = [] } = useItems();
  const { data: catalog = [] } = useCatalog(q, null);
  const { layout } = useLayout();
  const adopt = useChange(send.item, { onSuccess: onItem });
  useEffect(() => {
    if (open) {
      setQ("");
      setCreating(false);
    }
  }, [open]);

  const words = wordsOf(q);
  const mine = items
    .filter((i) => KIND_CATEGORIES[kind].includes(i.category as Category) && !i.archived)
    .filter((i) => itemMatches(i, q))
    .sort(
      (a, b) =>
        Number(dec(b.onHand.quantity).gt(ZERO)) - Number(dec(a.onHand.quantity).gt(ZERO)) ||
        a.displayName.localeCompare(b.displayName),
    );
  const fromCatalog = catalog
    .filter((p: CatalogProduct) => KIND_CATEGORIES[kind].includes(p.category as Category))
    .filter((p) => p.adoptedItemIds.length === 0)
    .slice(0, 12);
  const suggestedItem = suggestion?.item
    ? items.find((i) => i.id === suggestion.item?.id && !i.archived)
    : undefined;
  const suggestedProduct = suggestedItem ? null : (suggestion?.catalogProduct ?? null);
  const suggesting = !words.length && (suggestedItem || suggestedProduct);
  // The suggested item is listed once, above the rest.
  const listed = suggesting && suggestedItem ? mine.filter((i) => i.id !== suggestedItem.id) : mine;

  if (creating) {
    return (
      <Dialog open={open} onClose={onClose} title="Create an ingredient" wide>
        <NewItemForm
          initialName={q.trim()}
          categories={KIND_CATEGORIES[kind]}
          submitLabel={saveLabel}
          onAdded={onItem}
          onBack={() => setCreating(false)}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={title} wide>
      <div className="picker">
        {note ? (
          <p className="muted" style={{ margin: 0 }}>
            {note}
          </p>
        ) : null}
        {onKind ? (
          <div
            className="segmented"
            role="tablist"
            aria-label="Kind of ingredient"
            onKeyDown={onTabKeys}
          >
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={kind === k}
                tabIndex={kind === k ? 0 : -1}
                className={kind === k ? "active" : ""}
                onClick={() => onKind(k)}
              >
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
        ) : null}
        <label className="picker-search" htmlFor={searchId}>
          <Search size={16} aria-hidden />
          <input
            id={searchId}
            className="input"
            placeholder="Search your inventory and the catalog"
            value={q}
            autoFocus
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <ErrorNote error={adopt.error} />
        {suggesting ? (
          <>
            <h3>Suggested</h3>
            <ul className="picker-list">
              <li>
                {suggestedItem ? (
                  <button type="button" onClick={() => onItem(suggestedItem)}>
                    <span>
                      {suggestedItem.displayName}
                      {hopDetail(suggestedItem) ? (
                        <span className="faint"> · {hopDetail(suggestedItem)}</span>
                      ) : null}
                    </span>
                    <span className="faint">
                      {suggestion?.item?.via === "remembered"
                        ? "Linked this way before"
                        : "Suggested from its name, check it"}
                    </span>
                  </button>
                ) : suggestedProduct ? (
                  <button
                    type="button"
                    disabled={adopt.isPending}
                    onClick={() =>
                      adopt.mutate({ source: "catalog", catalogProductId: suggestedProduct.id })
                    }
                  >
                    <span>{suggestedProduct.name}</span>
                    <span className="faint">In the catalog, not stocked here yet</span>
                  </button>
                ) : null}
              </li>
            </ul>
          </>
        ) : null}
        <h3>{listed === mine ? "In your inventory" : "Anything else in your inventory"}</h3>
        {listed.length === 0 ? (
          <p className="faint">
            {words.length
              ? "Nothing here by that name."
              : listed === mine
                ? "Nothing of this kind yet."
                : "Nothing else of this kind."}
          </p>
        ) : (
          <ul className="picker-list">
            {listed.slice(0, 30).map((it) => (
              <li key={it.id}>
                <button type="button" onClick={() => onItem(it)}>
                  <span>
                    {it.displayName}
                    {hopDetail(it) ? <span className="faint"> · {hopDetail(it)}</span> : null}
                  </span>
                  <span className="faint num">
                    {dec(it.onHand.quantity).gt(ZERO)
                      ? `${showOnHand(it, layout.inventory.quantityMode)} on hand`
                      : "none on hand"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {words.length ? (
          <>
            <h3>In the catalog</h3>
            {fromCatalog.length === 0 ? (
              <p className="faint">Nothing in the catalog by that name.</p>
            ) : (
              <ul className="picker-list">
                {fromCatalog.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      disabled={adopt.isPending}
                      onClick={() => adopt.mutate({ source: "catalog", catalogProductId: p.id })}
                    >
                      <span>
                        {p.manufacturer ? `${p.manufacturer} ` : ""}
                        {p.name}
                      </span>
                      <span className="faint">
                        {[p.hop ? HOP_FORM_LABELS[p.hop.form] : null, p.origin]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="picker-create">
              <button type="button" className="button" onClick={() => setCreating(true)}>
                <Plus size={16} /> Create “{q.trim()}”
              </button>
              <span className="faint">
                Saved to your inventory with its details, to pick again.
              </span>
              {onNameOnly ? (
                <button type="button" className="link-button" onClick={() => onNameOnly(q.trim())}>
                  Only name it in this recipe
                </button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
